/**
 * Rolling a treatment plan out across the year is a count of plans plus a gap in
 * weeks between them. It is deliberately not a recurrence or a frequency: a gap of
 * two means two empty weeks sit between one plan ending and the next one starting,
 * which is not the same as starting a plan every two weeks.
 *
 * A gap of zero is valid and means consecutive weeks with no break.
 */

export interface TreatmentPlanRolloutRequest {
  /** Week the first plan starts in. */
  startWeekNumber: number;
  /** How many plans the user asked for. */
  planCount: number;
  /** Empty weeks between one plan ending and the next starting. Zero is valid. */
  weeksBetweenPlans: number;
  /** Weeks the template itself covers beyond its first week (0 for a one-week plan). */
  planSpan: number;
  /** Last week the year holds. Plans that would start after it are dropped. */
  lastWeekNumber: number;
}

/** What the roll-out dialog collects from the user. */
export interface TreatmentPlanRolloutSelection {
  startWeekNumber: number;
  capacity: number;
  planCount: number;
  weeksBetweenPlans: number;
  lastWeekNumber: number;
}

export interface TreatmentPlanPlacement {
  startWeekNumber: number;
  endWeekNumber: number;
}

/**
 * Where each plan in the series lands. Fewer placements than `planCount` means the
 * series ran past the end of the year and the remainder was cut.
 *
 * The cut tests where a plan *starts*, not where it ends, so a multi-week plan starting
 * in one of the year's last weeks is still placed and runs past year end. That is the
 * long-standing behaviour and REH-307 asks for it to be kept.
 */
export const calculateTreatmentPlanPlacements = (
  request: TreatmentPlanRolloutRequest
): TreatmentPlanPlacement[] => {
  const { startWeekNumber, planCount, weeksBetweenPlans, planSpan, lastWeekNumber } = request;

  const placements: TreatmentPlanPlacement[] = [];
  let currentStart = startWeekNumber;

  for (let index = 0; index < planCount; index++) {
    if (currentStart > lastWeekNumber) break;

    const endWeekNumber = currentStart + planSpan;
    placements.push({ startWeekNumber: currentStart, endWeekNumber });
    currentStart = endWeekNumber + weeksBetweenPlans + 1;
  }

  return placements;
};

/** Weeks a template covers beyond its first week. A one-week template spans 0. */
export const calculateTemplateSpan = (weekNumbers: number[]): number => {
  if (weekNumbers.length === 0) return 0;
  return Math.max(...weekNumbers) - Math.min(...weekNumbers);
};

/**
 * Which sentence the summary needs. The count and the gap each change how the series
 * reads in English and Danish, so the wording is picked here and the caller maps the
 * variant to a translation key.
 */
export type TreatmentPlanRolloutSummaryVariant = 'single' | 'consecutive' | 'gapOne' | 'gap';

export interface TreatmentPlanRolloutSummary {
  variant: TreatmentPlanRolloutSummaryVariant;
  /** Interpolation values for the variant's translation key. */
  values: {
    planCount: number;
    gap: number;
    startWeek: number;
    endWeek: number;
  };
}

/**
 * What the dialog tells the user it is about to add. Derived from the placements the
 * roll-out itself will use, so the two can never disagree: a series cut short by the
 * end of the year reports the count that actually fits.
 *
 * `endWeek` is clamped to the year's last week. A plan is placed on its start week
 * alone, so the final plan of a series can run past year end — but the year plan has
 * no weeks to put it in, so naming a week the year does not have would promise more
 * than gets added.
 *
 * Returns null when there is nothing to describe.
 */
export const buildTreatmentPlanRolloutSummary = (
  placements: TreatmentPlanPlacement[],
  weeksBetweenPlans: number,
  lastWeekNumber: number
): TreatmentPlanRolloutSummary | null => {
  if (placements.length === 0) return null;

  const fittedCount = placements.length;
  const values = {
    planCount: fittedCount,
    gap: weeksBetweenPlans,
    startWeek: placements[0].startWeekNumber,
    endWeek: Math.min(placements[fittedCount - 1].endWeekNumber, lastWeekNumber)
  };

  if (fittedCount === 1) return { variant: 'single', values };
  if (weeksBetweenPlans === 0) return { variant: 'consecutive', values };
  if (weeksBetweenPlans === 1) return { variant: 'gapOne', values };
  return { variant: 'gap', values };
};
